/* Shared DatePicker — iter79.
 * Wraps shadcn Popover + Calendar to replace native <input type="date">.
 * Controlled by a `value` (ISO YYYY-MM-DD) + `onChange(iso)` API. */
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';
import { CalendarBlank } from '@phosphor-icons/react';
import { fr } from 'date-fns/locale';

const parseIso = (iso) => {
  if (!iso) return undefined;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

const toIso = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export function DatePicker({
  value, onChange, placeholder = 'JJ/MM/AAAA',
  disabled = false, className = '', testid,
  fromYear, toYear,
}) {
  const date = parseIso(value);
  const currentYear = new Date().getFullYear();
  // Default : ±80 years around today (covers birthdates + distant plans).
  const _fromYear = fromYear ?? currentYear - 80;
  const _toYear = toYear ?? currentYear + 10;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" disabled={disabled}
          className={`h-9 justify-start font-normal ${
            !date ? 'text-[#94A3B8]' : 'text-[#0F172A]'
          } ${className}`}
          data-testid={testid}
        >
          <CalendarBlank size={14} className="mr-2 text-[#64748B]" />
          {date ? date.toLocaleDateString('fr-FR') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single" selected={date}
          onSelect={(d) => onChange && onChange(toIso(d))}
          captionLayout="dropdown-buttons"
          locale={fr}
          fromYear={_fromYear} toYear={_toYear}
          defaultMonth={date || new Date()}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export function DateRangePicker({
  from, to, onChange, placeholder = 'Sélectionner une plage',
  disabled = false, className = '', testid,
  fromYear, toYear,
}) {
  const fromDate = parseIso(from);
  const toDate = parseIso(to);
  const currentYear = new Date().getFullYear();
  const _fromYear = fromYear ?? currentYear - 5;
  const _toYear = toYear ?? currentYear + 5;
  const label = fromDate && toDate
    ? `${fromDate.toLocaleDateString('fr-FR')} → ${toDate.toLocaleDateString('fr-FR')}`
    : fromDate
      ? `${fromDate.toLocaleDateString('fr-FR')} → …`
      : placeholder;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" disabled={disabled}
          className={`h-9 justify-start font-normal ${
            !fromDate ? 'text-[#94A3B8]' : 'text-[#0F172A]'
          } ${className}`}
          data-testid={testid}
        >
          <CalendarBlank size={14} className="mr-2 text-[#64748B]" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={{ from: fromDate, to: toDate }}
          onSelect={(range) => {
            if (!onChange) return;
            onChange({
              from: toIso(range?.from),
              to: toIso(range?.to),
            });
          }}
          captionLayout="dropdown-buttons"
          locale={fr}
          fromYear={_fromYear} toYear={_toYear}
          defaultMonth={fromDate || new Date()}
          numberOfMonths={2}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
